<template>
  <div>
    <div class="role-responsibilities-tab">
      <RoleResponsibilitiesTable
        :dataStream="responsibilities"
        :chatterData="chatterData"
        :totalChatterData="totalChatterData"
        :paginationModel="paginationModel"
        :roleId="props.roleId"
        :roleName="roleData?.name || ''"
        :roleCode="
          roleData?.code_str
            ? roleData.code_str.split(' ')[0]
            : roleData?.code
              ? `#${roleData.code}`
              : ''
        "
        :drilldownInPanel="drilldownInPanel"
        :loading="loading"
        :allow-drag-responsibility-to-tree="allowDragResponsibilityToTree"
        :enable-responsibilities-reorder="enableResponsibilitiesReorder"
        :on-responsibility-drag-start-callback="onResponsibilityDragStartCallback"
        :on-responsibility-drag-end-callback="onResponsibilityDragEndCallback"
        @drilldown="handleDrilldownFromTable"
        @openChatterDrawer="handleOpenChatterDrawer"
        @requestMoreChatter="handleRequestMoreChatter"
        @optionClick="handleOptionClick"
        @onAttachResponsibilities="handleOpenAttachResponsibilities"
        @onRequest="handlePagination"
        @searchTextData="handleSearchTextData"
        @appliedFilterFromAPI="handleAppliedFilters"
        @onClear="handleClearFilters"
        @onAddIQ="handleAddIQAction"
        @openAttachedRoles="handleOpenAttachedRoles"
        @onReorder="handleReorder"
        @onDetach="handleDetachFromList"
      />
    </div>

    <!-- Rename Responsibility Dialog -->
    <RenameResponsibilityDialog
      v-if="actions.showRenameResponsibilityDialog && renameResponsibilityModel"
      v-model="actions.showRenameResponsibilityDialog"
      :responsibility="renameResponsibilityModel"
      @saved="handleResponsibilityRenamed"
    />

    <!-- Activate Responsibility Dialog -->
    <ActivateResponsibility
      v-model="actions.showActivateResponsibilityDialog"
      :responsibility="currentResponsibilityName"
      :employee-count="activationEmployeeCount"
      @onSave="handleActivateResponsibility"
    />

    <!-- Retire Responsibility Dialog -->
    <RetireResponsibility
      v-model="actions.showRetireResponsibilityDialog"
      :responsibility="currentResponsibilityName"
      @onSave="handleRetireResponsibility"
    />

    <!-- Re-Activate Responsibility Dialog -->
    <ReActivateResponsibility
      v-model="actions.showReActivateResponsibilityDialog"
      :responsibility="currentResponsibilityName"
      :employee-count="activationEmployeeCount"
      @onSave="handleReActivateResponsibility"
    />

    <!-- Delete Responsibility Dialog -->
    <DeleteResponsibility
      v-model="actions.deleteResponsibilityDialog"
      :responsibility="currentResponsibilityName"
      @onDelete="handleDeleteResponsibility"
    />

    <!-- Archive/Unarchive Dialog -->
    <ArchivePopUp
      :action="archiveAction"
      type="responsibility"
      :show="actions.showArchiveDialog"
      :curretObj="currentResponsibility"
      @onClose="actions.showArchiveDialog = false"
      @onArchive="handleArchiveResponsibility"
    />

    <!-- Attach Role Dialog -->
    <AttachedResponsibilityToRole
      v-if="actions.showAttachRoleDialog"
      v-model="actions.showAttachRoleDialog"
      OrgObjectType="Responsibility to Role"
      @onSave="handleAttachResponsibilityToRole"
      :type="showAttachResponsibilitiesPicker ? 'respToRole_new_picker' : 'respToRole'"
      :items="attachableRoles"
      @item-selected="handleItemSelected"
      @add-item-selected="handleAddNewRole"
      :parent-name="currentResponsibilityName"
      :hasMoreData="hasMoreData"
      :isLoadingMore="isLoadingMore"
      @load-more="loadMoreRoles"
      @search="searchRoles"
      :isAssigning="isAssigningObject"
    />

    <!-- Attach Responsibilities to Role Dialog -->
    <AttachedResponsibility
      v-if="attachResponsibilityDialog"
      v-model="attachResponsibilityDialog"
      @onSave="handleAttachResponsibilityToRoleFromList"
      :items="attachableResponsibilitiesList"
      @item-selected="handleResponsibilityItemSelected"
      @add-item-selected="handleAddNewResponsibility"
      :parent-name="roleName"
      :openDefault="openDefaultAttachDialog"
      @input="closeAttachResponsibilityDialog"
      :type="
        showAttachResponsibilitiesPicker
          ? 'attach_responsibility_role_new_picker'
          : 'attach_responsibility_role'
      "
      @load-more="loadMoreAttachableResponsibilities"
      @search="searchAttachableResponsibilities"
      :hasMoreData="hasMoreResponsibilitiesData"
      :isLoadingMore="isLoadingMoreResponsibilities"
      :isAssigning="isAssigningResponsibility"
    />

    <!-- Detach Roles Dialog -->
    <DetachOrgObjectsDialog
      :dialog="showDetachDialog"
      :loader="detachDialogLoader"
      :objectType="detachDialogData.objectType"
      :objectCode="detachDialogData.objectCode"
      :objectName="detachDialogData.objectName"
      :orgObjectItems="detachDialogData.attachItems"
      @detach="handleDetachFromRole"
      @cancel="closeDetachDialog"
    />

    <!-- Detach Role Confirmation Dialog -->
    <DetachRoleDialog
      v-model="showDetachRoleConfirmDialog"
      :role_name="roleToDetach?.name || ''"
      :parent_name="detachDialogData.objectName"
      @onAction="confirmDetachRole"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, getCurrentInstance, nextTick } from "vue";
import RoleResponsibilitiesTable from "@components/RoleResponsibilitiesTable/RoleResponsibilitiesTable.vue";
import RenameResponsibilityDialog from "@components/RolesCatalog/RenameResponsibilityDialog/RenameResponsibilityDialog.vue";
import { toRenameResponsibilityModel } from "@components/utils/renameResponsibilityModel";
import ActivateResponsibility from "@components/RolesCatalog/ActivateResponsibility.vue";
import RetireResponsibility from "@components/RolesCatalog/RetireResponsibility.vue";
import ReActivateResponsibility from "@components/RolesCatalog/ReActivateResponsibility.vue";
import DeleteResponsibility from "@components/RolesCatalog/DeleteResponsibility.vue";
import ArchivePopUp from "@components/common/ArchivePopUp.vue";
import AttachedResponsibilityToRole from "@components/RolesCatalog/AttachedResponsibilityToRole.vue";
import AttachedResponsibility from "@components/RolesCatalog/AttachedRole.vue";
import DetachOrgObjectsDialog from "@components/DetachOrgObjectsDialog/DetachOrgObjectsDialog.vue";
import DetachRoleDialog from "@components/DetachRoleDialog/DetachRoleDialog.vue";
import { fetchResponsibilityActivationCount } from "@/utils/activationEmployeeCount";
import {
  getListofResponsibilityV2,
  getChatterList,
  updateActivateResponsibility,
  updateRetireResponsibility,
  updateReActivateResponsibility,
  deleteResponsibility,
  updateArchiveResponsibility,
  updateUnArchiveResponsibility,
  attachResponsibilityToRole,
  fetchAttachableRoles,
  getRole,
  fetchAttachableResponsibilities,
  getResponsibilityAttachments,
  detachAttachments,
  reOrderSequenceData,
} from "@/api";
import { useIQOverlayStore } from "@/store/pinia/iqOverlay";
import { useIQChatStore } from "@components/IQOverlay/store/iqChatStore";
import {
  buildResponsibilityAttachFailedTranscriptMessage,
  buildResponsibilityCreatedTranscriptMessage,
  buildResponsibilityRejectedTranscriptMessage,
} from "@components/IQOverlay/responsibilitySuggestionTranscript";
import {
  CREATE_ATTACH_ORPHANED,
  CreateAndAttachError,
  createAndAttachResponsibility,
} from "@/api/createAndAttachResponsibility";
import { getFilters } from "@components/helper/localStorageHelper";
import { getDeleteErrorMessage } from "@components/utils/getDeleteErrorMessage";

const props = defineProps({
  roleId: {
    type: [Number, String],
    required: true,
  },
  /** When true (e.g. Positions Tree View), drilldown stays in same panel */
  drilldownInPanel: {
    type: Boolean,
    default: false,
  },
  allowDragResponsibilityToTree: {
    type: Boolean,
    default: false,
  },
  enableResponsibilitiesReorder: {
    type: Boolean,
    default: true,
  },
  onResponsibilityDragStartCallback: {
    type: Function,
    default: null,
  },
  onResponsibilityDragEndCallback: {
    type: Function,
    default: null,
  },
});

const emit = defineEmits([
  "roleDataUpdated",
  "drilldown",
  "role-attached-responsibilities-sync",
  "pane-after-responsibility-attached",
]);

function emitRoleAttachedResponsibilitiesSync() {
  const ids = (responsibilities.value || [])
    .map((row) => row.realId ?? row.data?.id)
    .filter((id) => id != null)
    .map((id) => String(id));
  emit("role-attached-responsibilities-sync", {
    roleId: props.roleId,
    responsibilityIds: ids,
  });
}

function handleDrilldownFromTable(data) {
  if (typeof console !== "undefined" && console.log) {
    console.log("[RespDrilldown] RoleResponsibilitiesTab received drilldown", data);
  }
  emit("drilldown", data);
}

const accountId = ref(null);
const responsibilities = ref([]);
const chatterData = ref([]);
const totalChatterData = ref(0);
const currentResponsibility = ref(null);
const renameResponsibilityModel = computed(() =>
  toRenameResponsibilityModel(currentResponsibility.value)
);
const archiveAction = ref(null);
const roleData = ref(null);
const attachableRoles = ref([]);
const hasMoreData = ref(false);
const isLoadingMore = ref(false);
const isAssigningObject = ref(false);
const attachmentId = ref(null);
const currentPage = ref(1);
const totalCount = ref(0);
const currentResponsibilityId = ref(null);
const attachResponsibilityDialog = ref(false);
const attachableResponsibilitiesList = ref([]);
const hasMoreResponsibilitiesData = ref(false);
const isLoadingMoreResponsibilities = ref(false);
const isAssigningResponsibility = ref(false);
const selectedResponsibilityAttachmentId = ref(null);
const responsibilitiesPage = ref(1);
const responsibilitiesSearchTerm = ref("");
const responsibilitiesLimit = 10;
const openDefaultAttachDialog = ref(false);
const roleName = ref("");
const showDetachDialog = ref(false);
const detachDialogLoader = ref(false);
const detachDialogData = ref({
  objectType: "",
  objectCode: "",
  objectName: "",
  attachItems: [],
});
const currentResponsibilityForDetach = ref(null);
const showDetachRoleConfirmDialog = ref(false);
const roleToDetach = ref(null);
const { proxy } = getCurrentInstance();

const paginationModel = ref({
  page: 1,
  total: 0,
  perPage: 10,
});
const currentSearchTerm = ref("");
const appliedFilters = ref([]);
// ER-10591 — parent's fetch sets this; gates RoleResponsibilitiesTable's skeleton (flag-gated).
const loading = ref(false);

const showAttachResponsibilitiesPicker = computed(() => {
  return proxy.$store.getters["featureFlags/ideaMDP389"];
});

const responsibilitySuggestionTranscriptEnabled = computed(() => {
  return proxy.$store.getters["featureFlags/ideaMDP577"];
});

const activationEmployeeCount = ref(0);

const actions = ref({
  showRenameResponsibilityDialog: false,
  showActivateResponsibilityDialog: false,
  showRetireResponsibilityDialog: false,
  showReActivateResponsibilityDialog: false,
  deleteResponsibilityDialog: false,
  showArchiveDialog: false,
  showAttachRoleDialog: false,
});

const currentResponsibilityName = computed(() => {
  return currentResponsibility.value?.data?.name || "";
});

const iqOverlayStore = useIQOverlayStore();
const iqChatStore = useIQChatStore();

let unsubscribeCreateAndAttach = null;
let unsubscribeReject = null;
let unsubscribeSuggestMore = null;
let unsubscribeEdit = null;

onMounted(async () => {
  accountId.value = JSON.parse(localStorage.getItem("currentAccountID"));

  // Load saved filters from localStorage before fetching data
  const savedFilters = getFilters("RoleResponsibilities");
  if (savedFilters && savedFilters.length > 0) {
    appliedFilters.value = savedFilters;
  }

  try {
    proxy.$loader.show();
    loading.value = true;
    await getRoleData();

    const responsibilityCount = roleData.value?.attachments_count?.responsibility || 0;
    const limit = responsibilityCount > 0 ? responsibilityCount : paginationModel.value.perPage;

    await fetchResponsibilities({
      page: 1,
      limit: limit,
      search: currentSearchTerm.value,
      filters: appliedFilters.value,
      include_badge_holders: true,
    });
  } catch (error) {
    console.error("Error loading role responsibilities data:", error);
  } finally {
    loading.value = false;
    proxy.$loader.hide();
  }

  unsubscribeCreateAndAttach = iqOverlayStore.onCreateAndAttach(handleCreateAndAttachSuggestion);
  unsubscribeReject = iqOverlayStore.onReject(handleRejectSuggestion);
  unsubscribeSuggestMore = iqOverlayStore.onSuggestMore(handleSuggestMore);
  unsubscribeEdit = iqOverlayStore.onEdit(handleEditSuggestion);
});

onBeforeUnmount(() => {
  if (unsubscribeCreateAndAttach) unsubscribeCreateAndAttach();
  if (unsubscribeReject) unsubscribeReject();
  if (unsubscribeSuggestMore) unsubscribeSuggestMore();
  if (unsubscribeEdit) unsubscribeEdit();
});

const getRoleData = async () => {
  const { data } = await getRole(accountId.value, props.roleId);
  roleData.value = data;
};

const transformData = (apiData) => {
  return apiData.map((item, index) => {
    const validCoaches = (item.coaches || []).filter((coach) => coach.id && coach.name);
    const people =
      validCoaches.length > 0
        ? validCoaches.map((coach) => ({
            firstName: coach.name?.split(" ")[0] || "",
            lastName: coach.name?.split(" ").slice(1).join(" ") || "",
            img: coach.avatar,
            id: coach.id,
            level: coach.level,
            badge_id: coach.badge_id,
          }))
        : null;

    const masters = (item.masters || []).map((m) => {
      let firstName = String(m.first_name ?? "").trim();
      let lastName = String(m.last_name ?? "").trim();
      if (!firstName && !lastName) {
        const parts = String(m.full_name ?? m.name ?? "")
          .trim()
          .split(/\s+/)
          .filter(Boolean);
        firstName = parts[0] || "";
        lastName = parts.slice(1).join(" ");
      }
      return {
        id: m.id,
        firstName,
        lastName,
        img: m.avatar,
        level: m.level ?? m.badge_level,
        badge_id: m.badge_id,
      };
    });

    return {
      id: index + 1,
      parentId: -1,
      realId: item.id,
      data: {
        id: item.id,
        type: "responsibility",
        code: item.code,
        code_str: item.code_str || `#${item.code} - ${item.name}`,
        name: item.name,
        account: item.account,
        status: item.status || "inactive",
        retire: { status: item.status || "inactive" },
        people: people,
        masters: masters,
      },
      count_of_attachments_to_roles: item.count_role_attachments || 0,
      apprentice_active: item.active_apprentice_checkpoints || 0,
      apprentice_draft: item.draft_apprentice_checkpoints || 0,
      professional_active: item.active_professional_checkpoints || 0,
      professional_draft: item.draft_professional_checkpoints || 0,
      coach_active: item.active_coach_checkpoints || 0,
      coach_draft: item.draft_coach_checkpoints || 0,
      master_active: item.active_master_checkpoints || 0,
      master_draft: item.draft_master_checkpoints || 0,
      countOfNoviceAssigments: item.novice_assignment_count || 0,
      countOfApprenticeAssigments: item.apprentice_assignment_count || 0,
      countOfProfessionalAssignments: item.professional_assignment_count || 0,
      status: item.status || "inactive",
      retire: { status: item.status || "inactive" },
      type: "responsibility",
      types: "",
      has_items: false,
    };
  });
};

const handleAddIQAction = async () => {
  try {
    const { data: roleData } = await getRole(accountId.value, props.roleId);

    const roleCode = roleData?.code_str ? roleData.code_str.split(" ")[0] : `#${roleData?.code}`;
    const roleNameValue = roleData?.name || "";

    const prompt = `Pls suggest responsibilities for Role ${roleCode} - ${roleNameValue}`;

    iqOverlayStore.openOverlay({
      prompt,
      type: "roleResponsibility",
      roleName: `${roleCode} - ${roleNameValue}`,
      roleId: props.roleId ?? null,
      suggestedResponsibilities: [],
    });
  } catch (error) {
    console.error("Error opening IQ overlay:", error);
  }
};

const handleCreateAndAttachSuggestion = async (data) => {
  try {
    iqOverlayStore.setCreatingResponsibility(true);
    iqOverlayStore.setCreationError("");

    const payload = {
      name: data.responsibility.name,
      account: accountId.value,
      purpose: data.responsibility.purpose || "",
    };
    const newRes = await createAndAttachResponsibility({
      accountId: accountId.value,
      roleId: props.roleId,
      payload,
    });

    const successMessage = responsibilitySuggestionTranscriptEnabled.value
      ? buildResponsibilityCreatedTranscriptMessage({
          codeStr: newRes.data.code_str,
          id: newRes.data.id,
          name: newRes.data.name,
          suggestionName: data.responsibility.name,
        })
      : `Just created and attached Responsibility ${
          newRes.data.code_str || `#${newRes.data.id}`
        }`;
    iqChatStore.addAIMessage(successMessage);

    iqOverlayStore.removeSuggestion(data.index - 1);

    // Refresh role data to update the count
    await getRoleData();

    // Recalculate limit based on new total count, similar to onMounted
    const responsibilityCount = roleData.value?.attachments_count?.responsibility || 0;
    const limit =
      responsibilityCount > 0 && responsibilityCount <= 10
        ? responsibilityCount
        : paginationModel.value.perPage;

    // Reset to page 1 after attaching to show all items
    await getResponsibilities({
      page: 1,
      limit: limit,
      search: currentSearchTerm.value,
      filters: appliedFilters.value,
      include_badge_holders: true,
    });

    // Notify parent to refresh its roleData
    emit("roleDataUpdated");
  } catch (e) {
    console.error("Error creating and attaching responsibility:", e);

    let errorMessage = "";

    if (e instanceof CreateAndAttachError) {
      if (e.kind === CREATE_ATTACH_ORPHANED && responsibilitySuggestionTranscriptEnabled.value) {
        iqChatStore.addAIMessage(
          buildResponsibilityAttachFailedTranscriptMessage({
            codeStr: e.responsibility?.code_str,
            id: e.responsibility?.id,
            name: e.responsibility?.name,
            suggestionName: data.responsibility.name,
          })
        );
      }
      errorMessage = e.message;
    } else if (e.response?.data) {
      if (Array.isArray(e.response.data) && e.response.data.length > 0) {
        const nameError = e.response.data[0];
        if (typeof nameError === "string" && nameError.toLowerCase().includes("already exists")) {
          errorMessage = "Name already exists. Only unique names are allowed";
        } else if (typeof nameError === "string" && nameError.toLowerCase().includes("unique")) {
          errorMessage = "Name already exists. Only unique names are allowed";
        } else {
          errorMessage = nameError;
        }
      } else if (typeof (e.response.data.name ?? e.response.data.detail) === "string") {
        const nameError = e.response.data.name ?? e.response.data.detail;
        if (
          nameError.toLowerCase().includes("already exists") ||
          nameError.toLowerCase().includes("unique")
        ) {
          errorMessage = "Name already exists. Only unique names are allowed";
        } else {
          errorMessage = nameError;
        }
      }
    } else if (e.response?.data?.message) {
      errorMessage = e.response.data.message;
    } else if (e.message && !e.message.includes("status code")) {
      errorMessage = e.message;
    }

    iqOverlayStore.setCreationError(
      errorMessage || "The responsibility could not be created. Please try again."
    );
  } finally {
    iqOverlayStore.setCreatingResponsibility(false);
  }
};

const handleRejectSuggestion = (data) => {
  if (!responsibilitySuggestionTranscriptEnabled.value) {
    return;
  }
  const message = buildResponsibilityRejectedTranscriptMessage({
    name: data?.responsibility?.name,
  });
  iqChatStore.addAIMessage(message);
};

const handleSuggestMore = () => {
  console.log("Suggest more clicked");
  // The IQOverlay handles resubmitting the query internally
  // No additional action needed here
};

const handleEditSuggestion = (data) => {
  console.log("Edit suggestion:", data);
};

const fetchResponsibilities = async (params = {}) => {
  const { page = 1, limit = 10, search = "", filters = [], include_badge_holders = true } = params;

  const offset = (page - 1) * limit;

  const filtersObj = {
    role: props.roleId,
  };

  if (filters.includes("Show")) {
    filtersObj.archived = true;
  } else {
    filtersObj.archived = false;
  }

  // Handle status filters
  if (filters.length > 0) {
    const statusFilters = ["draft", "active", "retired", "inactive"];
    const statusFilterValues = filters.filter((f) => statusFilters.includes(f));
    if (statusFilterValues.length > 0) {
      filtersObj.status = statusFilterValues;
    }
  }

  // Don't pass ordering when filtering by role to maintain sequence order
  const ordering = filtersObj.role ? null : "name";

  const response = await getListofResponsibilityV2(
    accountId.value,
    limit,
    offset,
    search,
    filtersObj,
    ordering,
    include_badge_holders
  );

  const rawData = response.data.results || [];
  const totalCount = response.data.count || 0;

  if (totalCount > limit && totalCount > 0) {
    const retryResponse = await getListofResponsibilityV2(
      accountId.value,
      totalCount, // Use count as limit
      0, // offset = 0 since we want all records
      search,
      filtersObj,
      ordering,
      include_badge_holders
    );
    responsibilities.value = transformData(retryResponse.data.results || []);
  } else {
    responsibilities.value = transformData(rawData);
  }

  // Always use count from API as perPage to show all records on one page (no pagination)
  const perPageValue = totalCount > 0 ? totalCount : 10;

  paginationModel.value = {
    page: page,
    total: totalCount,
    perPage: perPageValue,
  };

  emitRoleAttachedResponsibilitiesSync();
};

const getResponsibilities = async (params = {}) => {
  try {
    proxy.$loader.show();
    loading.value = true;
    await fetchResponsibilities(params);
  } catch (error) {
    console.error("Error fetching responsibilities:", error);
  } finally {
    loading.value = false;
    proxy.$loader.hide();
  }
};

const handleOpenChatterDrawer = async (responsibilityData) => {
  try {
    chatterData.value = [];
    totalChatterData.value = 0;

    const responsibilityId = responsibilityData.id;
    const page = 1;

    const { data } = await getChatterList(
      accountId.value,
      responsibilityId,
      "responsibility",
      page
    );

    if (data && data.count) {
      totalChatterData.value = data.count;
    }

    if (data && data.results) {
      const chatterItems = data.results.map((el) => {
        const date = new Date(el.recorded_on);
        const milliseconds = date.getTime();
        const millisecondsString = milliseconds.toString();
        const nameParts = el.recorded_by_name ? el.recorded_by_name.split(" ") : [""];

        const newEl = {
          id: el.id,
          avatar:
            nameParts.length >= 2
              ? {
                  picture: "",
                  firstName: nameParts[0],
                  lastName: nameParts.slice(1).join(" "),
                }
              : {
                  picture: "",
                  firstName: el.recorded_by_name || "",
                  lastName: "",
                },
          IsExIQtiveBot: false,
          message: el.note,
          created_on: millisecondsString,
          IsHistory: el.is_history,
        };

        return newEl;
      });

      chatterData.value.push(...chatterItems);
    }
  } catch (error) {
    console.error("Error loading chatter data:", error);
  }
};

const handleRequestMoreChatter = async (responsibilityData) => {
  try {
    const responsibilityId = responsibilityData.id;
    const page = Math.floor(chatterData.value.length / 10) + 1;

    const { data } = await getChatterList(
      accountId.value,
      responsibilityId,
      "responsibility",
      page
    );

    if (data && data.results) {
      const chatterItems = data.results.map((el) => {
        const date = new Date(el.recorded_on);
        const milliseconds = date.getTime();
        const millisecondsString = milliseconds.toString();
        const nameParts = el.recorded_by_name ? el.recorded_by_name.split(" ") : [""];

        const newEl = {
          id: el.id,
          avatar:
            nameParts.length >= 2
              ? {
                  picture: "",
                  firstName: nameParts[0],
                  lastName: nameParts.slice(1).join(" "),
                }
              : {
                  picture: "",
                  firstName: el.recorded_by_name || "",
                  lastName: "",
                },
          IsExIQtiveBot: false,
          message: el.note,
          created_on: millisecondsString,
          IsHistory: el.is_history,
        };

        return newEl;
      });

      chatterData.value.push(...chatterItems);
    }
  } catch (error) {
    console.error("Error loading more chatter data:", error);
  }
};

const handleOptionClick = async (event) => {
  const { item, data } = event;
  // Set current responsibility for all actions
  currentResponsibility.value = {
    id: data.id,
    realId: data.realId || data.id,
    data: data.data,
    info: data.info,
    type: "responsibility",
  };

  switch (item.value) {
    case "activate_responsibility":
      await openActivationConfirmation("activate");
      break;
    case "retire_responsibility":
      actions.value.showRetireResponsibilityDialog = true;
      break;
    case "re_activate_responsibility":
      await openActivationConfirmation("reactivate");
      break;
    case "rename_responsibility":
      actions.value.showRenameResponsibilityDialog = true;
      break;
    case "attach_role":
      currentResponsibilityId.value = currentResponsibility.value.realId;
      getListOfRoles(currentResponsibilityId.value);
      actions.value.showAttachRoleDialog = true;
      break;
    case "delete_responsibility":
      actions.value.deleteResponsibilityDialog = true;
      break;
    case "archive_action":
      archiveAction.value = "archive";
      actions.value.showArchiveDialog = true;
      break;
    case "unarchive_action":
      archiveAction.value = "unarchive";
      actions.value.showArchiveDialog = true;
      break;
    default:
      break;
  }
};

const handleResponsibilityRenamed = async () => {
  try {
    proxy.$loader.setAppLoading(true);
    currentResponsibility.value = null;
    await getResponsibilities({
      page: paginationModel.value.page,
      limit: paginationModel.value.perPage,
      search: currentSearchTerm.value,
      filters: appliedFilters.value,
    });
  } catch (error) {
    console.error("Error refreshing responsibilities after rename:", error);
  } finally {
    proxy.$loader.setAppLoading(false);
  }
};

// ER-11818 — N > 0 opens the confirmation (with the count); N = 0 proceeds directly;
// an undeterminable preview (null) falls back to the pre-existing confirmation dialog.
const openActivationConfirmation = async (kind) => {
  const count = await fetchResponsibilityActivationCount(
    kind,
    accountId.value,
    currentResponsibility.value?.realId
  );
  activationEmployeeCount.value = count ?? 0;
  if (count === 0) {
    await (kind === "activate" ? handleActivateResponsibility() : handleReActivateResponsibility());
    return;
  }
  if (kind === "activate") actions.value.showActivateResponsibilityDialog = true;
  else actions.value.showReActivateResponsibilityDialog = true;
};

const handleActivateResponsibility = async () => {
  try {
    proxy.$loader.setAppLoading(true);
    const id = currentResponsibility.value?.realId;
    await updateActivateResponsibility(accountId.value, id);
    actions.value.showActivateResponsibilityDialog = false;
    currentResponsibility.value = null;
    await getResponsibilities({
      page: paginationModel.value.page,
      limit: paginationModel.value.perPage,
      search: currentSearchTerm.value,
      filters: appliedFilters.value,
    });
    emit("roleDataUpdated");
  } catch (error) {
    console.error("Error activating responsibility:", error);
  } finally {
    proxy.$loader.setAppLoading(false);
  }
};

const handleRetireResponsibility = async () => {
  try {
    proxy.$loader.setAppLoading(true);
    const id = currentResponsibility.value?.realId;
    const name = currentResponsibility.value?.data?.name;
    const payload = {
      status: "retired",
      name: name,
      account: accountId.value,
    };
    await updateRetireResponsibility(accountId.value, id, payload);
    actions.value.showRetireResponsibilityDialog = false;
    currentResponsibility.value = null;
    await getResponsibilities({
      page: paginationModel.value.page,
      limit: paginationModel.value.perPage,
      search: currentSearchTerm.value,
      filters: appliedFilters.value,
    });
    emit("roleDataUpdated");
  } catch (error) {
    console.error("Error retiring responsibility:", error);
  } finally {
    proxy.$loader.setAppLoading(false);
  }
};

const handleReActivateResponsibility = async () => {
  try {
    proxy.$loader.setAppLoading(true);
    const id = currentResponsibility.value?.realId;
    const name = currentResponsibility.value?.data?.name;
    const payload = {
      status: "active",
      name: name,
      account: accountId.value,
    };
    await updateReActivateResponsibility(accountId.value, id, payload);
    actions.value.showReActivateResponsibilityDialog = false;
    currentResponsibility.value = null;
    await getResponsibilities({
      page: paginationModel.value.page,
      limit: paginationModel.value.perPage,
      search: currentSearchTerm.value,
      filters: appliedFilters.value,
    });
    emit("roleDataUpdated");
  } catch (error) {
    console.error("Error re-activating responsibility:", error);
  } finally {
    proxy.$loader.setAppLoading(false);
  }
};

const handleDeleteResponsibility = async () => {
  try {
    proxy.$loader.setAppLoading(true);
    const id = currentResponsibility.value?.realId;
    await deleteResponsibility(accountId.value, id);
    actions.value.deleteResponsibilityDialog = false;
    currentResponsibility.value = null;
    await getResponsibilities({
      page: paginationModel.value.page,
      limit: paginationModel.value.perPage,
      search: currentSearchTerm.value,
      filters: appliedFilters.value,
    });
  } catch (error) {
    console.error("Error deleting responsibility:", error);
    proxy.$notifications.error(getDeleteErrorMessage(error, { entity: "responsibility" }));
  } finally {
    proxy.$loader.setAppLoading(false);
  }
};

const handleArchiveResponsibility = async () => {
  try {
    proxy.$loader.setAppLoading(true);
    const id = currentResponsibility.value?.realId;
    const objType = "responsibility";

    if (archiveAction.value === "archive") {
      await updateArchiveResponsibility(accountId.value, objType, id);
    } else if (archiveAction.value === "unarchive") {
      await updateUnArchiveResponsibility(accountId.value, objType, id);
    }

    actions.value.showArchiveDialog = false;
    currentResponsibility.value = null;
    archiveAction.value = null;
    await getResponsibilities({
      page: paginationModel.value.page,
      limit: paginationModel.value.perPage,
      search: currentSearchTerm.value,
      filters: appliedFilters.value,
    });
  } catch (error) {
    console.error("Error archiving/unarchiving responsibility:", error);
  } finally {
    proxy.$loader.setAppLoading(false);
  }
};

const getListOfRoles = async (responsibilityId, page = 1, searchTerm = "", isLoadMore = false) => {
  const limit = 10;
  const offset = (page - 1) * limit;
  const name = searchTerm || "";

  try {
    if (!isLoadMore) {
      attachableRoles.value = [];
    }
    isLoadingMore.value = true;
    const attachableeRoles = await fetchAttachableRoles(
      accountId.value,
      responsibilityId,
      limit,
      offset,
      name
    );
    const newItems = attachableeRoles.data.results.map((item) => ({
      id: item.id,
      code: item.code_str.match(/\d+/)[0],
      name: item.name,
      status: item.status,
      attached: item?.attached_to_responsibility,
    }));

    if (isLoadMore && attachableRoles.value) {
      attachableRoles.value = [...attachableRoles.value, ...newItems];
    } else {
      attachableRoles.value = newItems;
    }

    currentPage.value = page;
    totalCount.value = attachableeRoles.data.count || 0;
    // Check if there are more items based on actual items loaded, not theoretical page * limit
    hasMoreData.value = attachableRoles.value.length < totalCount.value;
  } catch (error) {
    console.error("Error fetching roles:", error);
  } finally {
    isLoadingMore.value = false;
  }
};

const handleItemSelected = (item) => {
  if (item && Object.prototype.hasOwnProperty.call(item, "id")) {
    attachmentId.value = item.id;
  }
};

const handleAddNewRole = async (item) => {
  try {
    proxy.$loader.show();
    const payload = {
      name: item.name,
      account: accountId.value,
    };
    const newRole = await proxy.$store.dispatch("readinessCatalogsStore/createRole", {
      role: payload,
      accountId: accountId.value,
    });
    attachmentId.value = newRole.id;
    await handleAttachResponsibilityToRole();
  } catch (error) {
    console.error("Error creating new role:", error);
  } finally {
    proxy.$loader.hide();
  }
};

const handleAttachResponsibilityToRole = async () => {
  try {
    isAssigningObject.value = true;
    proxy.$loader.setAppLoading(true);
    const responsibilityId = currentResponsibility.value?.realId;
    const targetRoleId = attachmentId.value;
    const respName = currentResponsibility.value?.data?.name || "";
    const respCodeStr = currentResponsibility.value?.data?.code_str || "";
    const payload = {
      role: attachmentId.value,
      responsibility: responsibilityId,
    };
    await attachResponsibilityToRole(accountId.value, attachmentId.value, {
      attachment_id: responsibilityId,
    });
    actions.value.showAttachRoleDialog = false;
    currentResponsibility.value = null;
    await getResponsibilities({
      page: paginationModel.value.page,
      limit: paginationModel.value.perPage,
      search: currentSearchTerm.value,
      filters: appliedFilters.value,
    });
    emit("roleDataUpdated");
    if (props.drilldownInPanel && responsibilityId != null && targetRoleId != null) {
      const targetRoleRow = attachableRoles.value?.find(
        (r) => String(r.id) === String(targetRoleId)
      );
      const targetRoleDisplay =
        targetRoleRow && targetRoleRow.code != null && targetRoleRow.name
          ? `#${targetRoleRow.code} - ${targetRoleRow.name}`
          : targetRoleRow?.name || "";
      await nextTick();
      emit("pane-after-responsibility-attached", {
        targetRoleId,
        responsibilityId,
        responsibilityName: respName,
        responsibilityCodeStr: respCodeStr,
        targetRoleName: targetRoleDisplay,
      });
    }
  } catch (error) {
    console.error("Error attaching role:", error);
  } finally {
    isAssigningObject.value = false;
    proxy.$loader.setAppLoading(false);
  }
};

const loadMoreRoles = async () => {
  if (isLoadingMore.value || !hasMoreData.value) {
    return;
  }
  await getListOfRoles(currentResponsibilityId.value, currentPage.value + 1, "", true);
};

const searchRoles = async (searchTerm) => {
  currentPage.value = 1;
  await getListOfRoles(currentResponsibilityId.value, 1, searchTerm, false);
};

const handleOpenAttachResponsibilities = async () => {
  attachResponsibilityDialog.value = true;
  openDefaultAttachDialog.value = true;
  responsibilitiesPage.value = 1;
  responsibilitiesSearchTerm.value = "";
  // Use roleData that's already fetched instead of fetching again
  roleName.value = roleData.value?.name || "";
  await fetchAttachableResponsibilitiesList();
};

const handleOpenAttachedRoles = async (rowData) => {
  const responsibilityId = rowData.realId || rowData.id;
  const responsibilityCode = rowData.data?.code_str || `#${rowData.data?.code}`;
  const responsibilityName = rowData.data?.name || "";

  currentResponsibilityForDetach.value = rowData;

  try {
    proxy.$loader.show();
    const response = await getResponsibilityAttachments(accountId.value, responsibilityId);

    const attachedRolesItems = response.data.results.map((role) => ({
      id: role.id,
      type: "role",
      code: true,
      codeValue: role.code ? `#${role.code}` : `#${role.id}`,
      name: role.name,
    }));

    detachDialogData.value = {
      objectType: "Responsibility",
      objectCode: responsibilityCode.split(" ")[0],
      objectName: responsibilityName,
      attachItems: attachedRolesItems,
    };

    showDetachDialog.value = true;

    proxy.$loader.hide();
  } catch (error) {
    console.error("Error fetching attached roles:", error);
    proxy.$loader.hide();
  }
};

const handleDetachFromRole = async (item) => {
  if (item.type.toLowerCase() === "role") {
    roleToDetach.value = item;
    showDetachRoleConfirmDialog.value = true;
  } else {
    // Original logic for other types if any
    await performDetach(item);
  }
};

const confirmDetachRole = async () => {
  if (roleToDetach.value) {
    await performDetach(roleToDetach.value);
    showDetachRoleConfirmDialog.value = false;
    roleToDetach.value = null;
  }
};

const performDetach = async (item) => {
  try {
    proxy.$loader.setAppLoading(true);
    detachDialogLoader.value = true;
    const responsibilityId =
      currentResponsibilityForDetach.value.realId || currentResponsibilityForDetach.value.id;
    const payload = {
      parent_id: item.id,
    };

    await detachAttachments(accountId.value, responsibilityId, payload);

    detachDialogData.value.attachItems = detachDialogData.value.attachItems.filter(
      (attachedItem) => attachedItem.id !== item.id
    );

    // Refresh role data to update the count
    await getRoleData();

    await getResponsibilities({
      page: paginationModel.value.page,
      limit: paginationModel.value.perPage,
      search: currentSearchTerm.value,
      filters: appliedFilters.value,
    });

    // Notify parent to refresh its roleData
    emit("roleDataUpdated");

    detachDialogLoader.value = false;
    proxy.$loader.setAppLoading(false);

    await nextTick();

    if (detachDialogData.value.attachItems.length === 0) {
      closeDetachDialog();
    }
  } catch (error) {
    console.error("Error detaching responsibility:", error);
    detachDialogLoader.value = false;
    proxy.$loader.setAppLoading(false);
  }
};

const handleDetachFromList = async (responsibilityData) => {
  try {
    proxy.$loader.setAppLoading(true);
    const responsibilityId = responsibilityData.realId || responsibilityData.id;
    const payload = {
      parent_id: props.roleId,
    };

    await detachAttachments(accountId.value, responsibilityId, payload);

    // Refresh role data to update the count
    await getRoleData();

    await getResponsibilities({
      page: paginationModel.value.page,
      limit: paginationModel.value.perPage,
      search: currentSearchTerm.value,
      filters: appliedFilters.value,
    });

    // Notify parent to refresh its roleData
    emit("roleDataUpdated");
  } catch (error) {
    console.error("Error detaching responsibility:", error);
  } finally {
    proxy.$loader.setAppLoading(false);
  }
};

const closeDetachDialog = () => {
  showDetachDialog.value = false;
  detachDialogLoader.value = false;
  currentResponsibilityForDetach.value = null;
  detachDialogData.value = {
    objectType: "",
    objectCode: "",
    objectName: "",
    attachItems: [],
  };
};

const fetchAttachableResponsibilitiesList = async (isLoadMore = false) => {
  try {
    if (isLoadMore) {
      isLoadingMoreResponsibilities.value = true;
    }

    const offset = (responsibilitiesPage.value - 1) * responsibilitiesLimit;
    const response = await fetchAttachableResponsibilities(
      accountId.value,
      props.roleId,
      responsibilitiesLimit,
      offset,
      responsibilitiesSearchTerm.value
    );

    const newItems = (response.data.results || []).map((item) => ({
      id: item.id,
      code: item.code_str.match(/\d+/)[0],
      name: item.name,
      status: item.status,
      attached: item?.attached_to_role,
    }));

    if (isLoadMore) {
      attachableResponsibilitiesList.value.push(...newItems);
    } else {
      attachableResponsibilitiesList.value = newItems;
    }

    hasMoreResponsibilitiesData.value = !!response.data.next;
  } catch (error) {
    console.error("Error fetching attachable responsibilities:", error);
    attachableResponsibilitiesList.value = [];
    hasMoreResponsibilitiesData.value = false;
  } finally {
    if (isLoadMore) {
      isLoadingMoreResponsibilities.value = false;
    }
  }
};

const handleResponsibilityItemSelected = (responsibility) => {
  if (!responsibility || !responsibility.id) return;
  selectedResponsibilityAttachmentId.value = responsibility.id;
};

const handleAddNewResponsibility = async (responsibilityData) => {
  try {
    proxy.$loader.show();

    const newResponsibility = await proxy.$store.dispatch(
      "readinessCatalogsStore/createResponsibility",
      { name: responsibilityData.name, accountId: accountId.value }
    );

    if (newResponsibility && newResponsibility.data && newResponsibility.data.id) {
      selectedResponsibilityAttachmentId.value = newResponsibility.data.id;
      await handleAttachResponsibilityToRoleFromList();
    }
  } catch (error) {
    console.error("Error creating and attaching responsibility:", error);
  } finally {
    proxy.$loader.hide();
  }
};

const handleAttachResponsibilityToRoleFromList = async () => {
  if (!selectedResponsibilityAttachmentId.value) return;

  const attachedResponsibilityId = selectedResponsibilityAttachmentId.value;
  const picked = attachableResponsibilitiesList.value.find(
    (x) => String(x.id) === String(attachedResponsibilityId)
  );
  const pickedLabel =
    picked && picked.code != null && picked.name ? `#${picked.code} - ${picked.name}` : "";

  try {
    isAssigningResponsibility.value = true;
    proxy.$loader.show();

    const payload = {
      attachment_id: attachedResponsibilityId,
    };

    await attachResponsibilityToRole(accountId.value, props.roleId, payload);

    attachResponsibilityDialog.value = false;
    selectedResponsibilityAttachmentId.value = null;
    responsibilitiesPage.value = 1;
    responsibilitiesSearchTerm.value = "";

    // Refresh role data to update the count
    await getRoleData();

    // Recalculate limit based on new total count, similar to onMounted
    const responsibilityCount = roleData.value?.attachments_count?.responsibility || 0;
    const limit =
      responsibilityCount > 0 && responsibilityCount <= 10
        ? responsibilityCount
        : paginationModel.value.perPage;

    // Reset to page 1 after attaching to show all items
    await getResponsibilities({
      page: 1,
      limit: limit,
      search: currentSearchTerm.value,
      filters: appliedFilters.value,
      include_badge_holders: true,
    });

    // Notify parent to refresh its roleData
    emit("roleDataUpdated");
    if (props.drilldownInPanel) {
      const targetRoleDisplay =
        roleData.value?.code_str ||
        (roleData.value?.code != null
          ? `#${roleData.value.code} - ${roleData.value.name || ""}`.trim()
          : roleData.value?.name || roleName.value || "");
      await nextTick();
      emit("pane-after-responsibility-attached", {
        targetRoleId: props.roleId,
        responsibilityId: attachedResponsibilityId,
        responsibilityName: picked?.name ?? "",
        responsibilityCodeStr: pickedLabel,
        targetRoleName: targetRoleDisplay,
      });
    }
  } catch (error) {
    console.error("Error attaching responsibility:", error);
  } finally {
    isAssigningResponsibility.value = false;
    proxy.$loader.hide();
  }
};

const loadMoreAttachableResponsibilities = async () => {
  if (isLoadingMoreResponsibilities.value || !hasMoreResponsibilitiesData.value) {
    return;
  }
  responsibilitiesPage.value += 1;
  await fetchAttachableResponsibilitiesList(true);
};

const searchAttachableResponsibilities = async (searchTerm) => {
  responsibilitiesPage.value = 1;
  responsibilitiesSearchTerm.value = searchTerm;
  await fetchAttachableResponsibilitiesList(false);
};

const closeAttachResponsibilityDialog = () => {
  attachResponsibilityDialog.value = false;
  selectedResponsibilityAttachmentId.value = null;
  responsibilitiesPage.value = 1;
  responsibilitiesSearchTerm.value = "";
  openDefaultAttachDialog.value = false;
};

const handlePagination = async (paginationData) => {
  await getResponsibilities({
    page: paginationData.page,
    limit: paginationModel.value.perPage,
    search: currentSearchTerm.value,
    filters: appliedFilters.value,
    include_badge_holders: true,
  });
};

const handleSearchTextData = async (data) => {
  currentSearchTerm.value = data.name;
  await getResponsibilities({
    page: 1,
    limit: paginationModel.value.perPage,
    search: data.name,
    filters: appliedFilters.value,
    include_badge_holders: true,
  });
};

const handleAppliedFilters = async (filters) => {
  // Only fetch if filters have actually changed
  const filtersChanged = JSON.stringify(appliedFilters.value) !== JSON.stringify(filters);

  if (filtersChanged) {
    try {
      await getResponsibilities({
        page: 1,
        limit: paginationModel.value.perPage,
        search: currentSearchTerm.value,
        filters: filters,
        include_badge_holders: true,
      });
      // Only update state after successful fetch
      appliedFilters.value = filters;
    } catch (error) {
      console.error("Error applying filters:", error);
      // State remains unchanged if fetch fails
    }
  }
};

const handleClearFilters = async () => {
  currentSearchTerm.value = "";
  appliedFilters.value = [];
  await getResponsibilities({
    page: 1,
    limit: paginationModel.value.perPage,
    search: "",
    filters: [],
    include_badge_holders: true,
  });
};

const handleReorder = async (reorderData) => {
  try {
    // Extract responsibility IDs from the reordered data
    const reorderedIds = reorderData.data
      .map((item) => item.realId || item.data?.id)
      .filter((id) => id != null);

    if (reorderedIds.length === 0) {
      console.warn("No valid responsibility IDs found for reordering");
      return;
    }

    const response = await reOrderSequenceData(accountId.value, props.roleId, reorderedIds);

    // Use the updated data returned from the API
    if (response?.data) {
      const rawData = response.data.results || response.data || [];
      responsibilities.value = transformData(rawData);

      // Update pagination if count is provided
      if (response.data.count !== undefined) {
        paginationModel.value = {
          ...paginationModel.value,
          total: response.data.count || 0,
        };
      }
      emitRoleAttachedResponsibilitiesSync();
    }
  } catch (error) {
    console.error("Error reordering responsibilities:", error);
  }
};

defineExpose({
  fetchResponsibilities: async () => {
    await getResponsibilities({
      page: paginationModel.value.page,
      limit: paginationModel.value.perPage,
      search: currentSearchTerm.value,
      filters: appliedFilters.value,
      include_badge_holders: true,
    });
  },
});
</script>
<style scoped>
.role-responsibilities-tab {
  background-color: #ffffff;
  box-shadow:
    0px 1px 2px 0px #1018280f,
    0px 1px 3px 0px #1018281a;
  border-radius: 10px;
}
</style>
